<script setup>
import BaseButton from "@/app/components/ui/BaseButton.vue"
import BaseTextField from "@/app/components/ui/BaseTextField.vue"
import CountrySelector from "@/app/components/ui/CountrySelector.vue"
import { useI18n } from "@/app/composables/useI18n"
import { camelToSnakeCaseWithNumbers } from "@/app/utils/strings"
import { fetchCountriesData } from "@/user-account/api"
import { useUserService } from "@/user-account/composables/useUserService"
import { rules as addressLine1Rules } from "@/user-account/utils/input-rules/addressLine1"
import { rules as addressLine2Rules } from "@/user-account/utils/input-rules/addressLine2"
import { rules as cityRules } from "@/user-account/utils/input-rules/city"
import { rules as dobRules } from "@/user-account/utils/input-rules/dob"
import { rules as firstNameRules } from "@/user-account/utils/input-rules/first-name"
import { rules as lastNameRules } from "@/user-account/utils/input-rules/last-name"
import { rules as postalCodeRules } from "@/user-account/utils/input-rules/postel-code"
import { rules as countryRules } from "@/user-account/utils/input-rules/select-country"
import { computed, ref, watch } from "vue"

const props = defineProps({
  regions: {
    type: Array,
    required: true,
  },
  personalData: {
    type: Object,
    default: () => ({}),
  },
})
const emit = defineEmits(["submit"])
const countries = ref([])

const { t } = useI18n()
const { user } = useUserService()

const displayOrders = [
  "firstName",
  "lastName",
  "dateOfBirth",
  "countryId",
  "addressLine1",
  "addressLine2",
  "city",
  "postalCode",
]

const inputs = ref([])

// Computed properties to find country-related input fields by name
const countryInput = computed(() => inputs.value.find(input => input.name === "countryId"))

async function fetchCountries() {
  try {
    const res = await fetchCountriesData()
    countries.value = res.data
  }
  catch (error) {
    console.error("Error fetching countries:", error)
  }
}

watch(
  () => [props.personalData],
  ([data]) => {
    if (data) {
      const fieldConfigs = {
        firstName: {
          type: "text",
          label: t("kyc.first_name"),
          value: data?.firstName,
          rule: firstNameRules,
        },
        lastName: {
          type: "text",
          label: t("kyc.last_name"),
          value: props.personalData?.lastName || "",
          rule: lastNameRules,
        },
        dateOfBirth: {
          type: "date",
          label: t("kyc.date_of_birth"),
          value: props.personalData?.dateOfBirth || "",
          rule: dobRules,
        },
        city: {
          type: "text",
          label: t("kyc.city"),
          value: props.personalData?.city || "",
          rule: cityRules,
        },
        addressLine1: {
          type: "text",
          label: t(`kyc.${camelToSnakeCaseWithNumbers("addressLine1")}`),
          value: props.personalData?.addressLine1 || "",
          rule: addressLine1Rules,
        },
        addressLine2: {
          type: "text",
          label: t(`kyc.${camelToSnakeCaseWithNumbers("addressLine2")}`),
          value: props.personalData?.addressLine2 || "",
          rule: addressLine2Rules,
        },
        postalCode: {
          type: "text",
          label: t("kyc.postal_code"),
          value: props.personalData?.postalCode || "",
          rule: postalCodeRules,
        },
        countryId: {
          label: t("kyc.country"),
          value: props.personalData?.countryId || "",
          rule: countryRules,
        },
      }

      displayOrders.forEach((field) => {
        inputs.value.push({
          name: field,
          value: props.personalData?.[field] || "",
          type: fieldConfigs[field].type,
          label: fieldConfigs[field].label,
          placeholder: fieldConfigs[field].label,
          rules: fieldConfigs[field].rule || [],
          clearable: false,
          error: "",
        })
      })
    }
  },
)

fetchCountries()

function handleUpdateCountry(id) {
  if (countryInput.value) {
    countryInput.value.value = id || ""
    countryInput.value.error = ""
  }
}
</script>

<template>
  <div v-if="user.kycLevel === 0">
    We need some basic info from you to proceed to deposit – will take less than a minute to complete.
  </div>
  <form @submit.prevent="emit('submit', inputs)">
    <div class="form-fields">
      <template v-for="input in inputs" :key="input.name">
        <!-- Regular text fields for everything except countryId -->
        <BaseTextField
          v-if="input.name !== 'countryId'"
          :model-value="input.value"
          :rules="input.rules"
          :label="input.label"
          :placeholder="input.placeholder"
          :type="input.type"
          :clearable="input.clearable"
          :icon-name="input.iconName"
          :error="input.error"
          @update:model-value="(value) => {
            input.value = value
            input.error = ''
          }"
          @error="input.error = $event"
        />

        <!-- Country selector for countryId -->
        <CountrySelector
          v-else-if="input.name === 'countryId' && countries.data"
          :custom-countries="countries.data"
          :country-id="input.value || ''"
          :error="input.error || ''"
          @error="input.error = $event"
          @country-selected="handleUpdateCountry"
        />
      </template>
    </div>

    <BaseButton class="submit alight wide high" type="submit">
      {{ t("kyc.сlick_to_complete") }}
    </BaseButton>
  </form>
</template>

<style lang="scss" scoped>
.form-fields {
  display: flex;
  margin-top: 1rem;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}
</style>
